import type { ReactNode } from "react";
import { motion, AnimatePresence, type Variants } from "motion/react";
import styles from "./EvaluationFooter.module.css";
import tickIcon from "@assets/icons/charm-tick.svg";
import {
  FOOTER_VIEW_MODE,
  type FooterViewMode,
} from "@constants/evaluationConstants";

interface EvaluationFooterProps {
  questionIndex: number;
  totalQuestions: number;
  isSingleQuestionTask: boolean;
  approvalValid: boolean;
  isSaving: boolean;
  isReportView?: boolean;
  showReportSummaryNav?: boolean;
  onPrevious: () => void;
  onNextFromReport?: () => void;
  onApproveAndSubmit: () => void;
  onApproveAndNext: () => void;
}

const SPRING_TRANSITION = {
  type: "spring" as const,
  stiffness: 300,
  damping: 25,
};

function resolveFooterViewMode({
  isReportView,
  isSingleQuestionTask,
}: {
  isReportView: boolean;
  isSingleQuestionTask: boolean;
}): FooterViewMode {
  if (isReportView) return FOOTER_VIEW_MODE.REPORT;
  if (isSingleQuestionTask) return FOOTER_VIEW_MODE.SINGLE_QUESTION;
  return FOOTER_VIEW_MODE.MULTI_QUESTION;
}

function FooterShell({ children }: { children: ReactNode }) {
  return (
    <div>
      <motion.div className={styles.evaluatorFooter} layout>
        {children}
      </motion.div>
    </div>
  );
}

function EvaluationFooter({
  questionIndex,
  totalQuestions,
  isSingleQuestionTask,
  approvalValid,
  isSaving,
  isReportView = false,
  showReportSummaryNav = false,
  onPrevious,
  onNextFromReport,
  onApproveAndSubmit,
  onApproveAndNext,
}: EvaluationFooterProps) {
  const viewMode = resolveFooterViewMode({ isReportView, isSingleQuestionTask });
  const isLastQuestion = questionIndex === totalQuestions - 1;
  const showPrevious =
    viewMode === FOOTER_VIEW_MODE.MULTI_QUESTION &&
    (questionIndex > 0 || (questionIndex === 0 && showReportSummaryNav));

  const tickVariants: Variants = approvalValid
    ? {
        initial: { scale: 1 },
        hover: {
          scale: 1.2,
          transition: { type: "spring", stiffness: 300, damping: 20 },
        },
      }
    : { initial: { scale: 1 }, hover: { scale: 1 } };

  const renderApprovalButton = (key: string, label: string, onClick: () => void) => (
    <motion.button
      key={key}
      className={styles.approvalButton}
      onClick={onClick}
      disabled={!approvalValid || isSaving}
      layout
      whileHover="hover"
      initial="initial"
    >
      {isSaving ? (
        <motion.div
          className={styles.spinner}
          animate={{ rotate: 360 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 15,
            repeat: Infinity,
            duration: 0.8,
          }}
        />
      ) : (
        <motion.img src={tickIcon} alt="Tick Icon" variants={tickVariants} />
      )}
      {label}
    </motion.button>
  );

  switch (viewMode) {
    case FOOTER_VIEW_MODE.REPORT:
      return (
        <FooterShell>
          <motion.button
            key="next-from-report"
            className={styles.nextButton}
            onClick={onNextFromReport}
            layout
            transition={SPRING_TRANSITION}
          >
            Next
          </motion.button>
        </FooterShell>
      );

    case FOOTER_VIEW_MODE.SINGLE_QUESTION:
      return (
        <FooterShell>
          {renderApprovalButton(
            "approve-submit",
            "Approve & Submit",
            onApproveAndSubmit,
          )}
          {renderApprovalButton(
            "approve-next",
            "Approve & Next",
            onApproveAndNext,
          )}
        </FooterShell>
      );

    case FOOTER_VIEW_MODE.MULTI_QUESTION:
      return (
        <FooterShell>
          <AnimatePresence>
            {showPrevious && (
              <motion.button
                key="previous"
                className={styles.previousButton}
                onClick={onPrevious}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                layout
                transition={SPRING_TRANSITION}
              >
                Previous
              </motion.button>
            )}
          </AnimatePresence>
          {isLastQuestion
            ? renderApprovalButton(
                "approve-submit",
                "Approve & Submit",
                onApproveAndSubmit,
              )
            : renderApprovalButton(
                "approve-next",
                "Approve & Next",
                onApproveAndNext,
              )}
        </FooterShell>
      );
  }
}

export default EvaluationFooter;
